import { useMemo } from 'react';

interface DropdownOption {
	id: string | number;
}

interface CurrentPeriodWeek {
	year?: number;
	period?: number;
	week?: number;
}

interface UseDisabledPeriodWeekIdsParams {
	currentPeriodWeek: CurrentPeriodWeek | null | undefined;
	selectedYearNumbers: number[];
	periodOptions: DropdownOption[];
	weekOptions: DropdownOption[];
}

const toNumericId = (value: string | number): number => {
	const parsed = Number(String(value).replace(/\D/g, ''));
	return Number.isNaN(parsed) ? 0 : parsed;
};

export function useDisabledPeriodWeekIds({
	currentPeriodWeek,
	selectedYearNumbers,
	periodOptions,
	weekOptions,
}: UseDisabledPeriodWeekIdsParams): {
	disabledPeriodIds: Set<string>;
	disabledWeekIds: Set<string>;
} {
	const disabledPeriodIds = useMemo(() => {
		if (!currentPeriodWeek?.year || !currentPeriodWeek?.period) return new Set<string>();
		if (selectedYearNumbers.length !== 1 || selectedYearNumbers[0] !== currentPeriodWeek.year) return new Set<string>();

		const disabled = new Set<string>();
		for (const opt of periodOptions) {
			const periodNum = toNumericId(opt.id);
			if (periodNum > currentPeriodWeek.period) {
				disabled.add(String(opt.id));
			}
		}
		return disabled;
	}, [currentPeriodWeek, periodOptions, selectedYearNumbers]);

	const disabledWeekIds = useMemo(() => {
		if (!currentPeriodWeek?.year || !currentPeriodWeek?.period || !currentPeriodWeek?.week) return new Set<string>();
		if (selectedYearNumbers.length !== 1 || selectedYearNumbers[0] !== currentPeriodWeek.year) return new Set<string>();

		const currentWeekInYear = (currentPeriodWeek.period - 1) * 4 + currentPeriodWeek.week;
		const disabled = new Set<string>();
		for (const opt of weekOptions) {
			const weekNum = toNumericId(opt.id);
			if (weekNum > currentWeekInYear) {
				disabled.add(String(opt.id));
			}
		}
		return disabled;
	}, [currentPeriodWeek, selectedYearNumbers, weekOptions]);

	return { disabledPeriodIds, disabledWeekIds };
}

export default useDisabledPeriodWeekIds;